/**
 * Content (blog) admin list page. super_admin + marketing — gated at route + nav
 * level. Lists posts across all statuses with quick filters and a "+ New
 * post" CTA.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BRAND } from '@fin/shared/brand';
import { POST_STATUSES, type PostStatus } from '@fin/shared/enums';

import {
  useAiGenerateFull,
  useAiGenerateHero,
  useContentBuilds,
  useContentPosts,
  useCreateContentPost,
  useDeleteContentPost,
  useRebuildContentPost,
  useTriggerFullBuild,
  useUpdateContentPost,
} from '../lib/queries';

const STATUS_BADGE: Record<PostStatus, { bg: string; fg: string }> = {
  draft: { bg: '#e7e5e4', fg: '#44403c' },
  scheduled: { bg: '#fef3c7', fg: '#92400e' },
  published: { bg: '#d1fae5', fg: '#065f46' },
  archived: { bg: '#e0e7ff', fg: '#3730a3' },
};

export default function ContentPage() {
  const [statusFilter, setStatusFilter] = useState<PostStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const navigate = useNavigate();
  const postsQ = useContentPosts(statusFilter === 'all' ? undefined : statusFilter);
  const buildsQ = useContentBuilds();
  const createPost = useCreateContentPost();
  const updatePost = useUpdateContentPost();
  const deletePost = useDeleteContentPost();
  const fullBuild = useTriggerFullBuild();
  const rebuildPost = useRebuildContentPost();
  const aiFullMut = useAiGenerateFull();
  const aiHeroMut = useAiGenerateHero();

  const [newSlug, setNewSlug] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiStep, setAiStep] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const recentBuilds = useMemo(() => (buildsQ.data ?? []).slice(0, 5), [buildsQ.data]);

  const allPosts = postsQ.data ?? [];
  const totalPages = Math.max(1, Math.ceil(allPosts.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visiblePosts = useMemo(
    () => allPosts.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [allPosts, safePage],
  );
  function changeStatus(s: PostStatus | 'all') {
    setStatusFilter(s);
    setPage(1);
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const post = await createPost.mutateAsync({
        slug: newSlug.trim(),
        title: newTitle.trim(),
        description: newDescription.trim(),
        body: '',
      });
      setCreating(false);
      setNewSlug('');
      setNewTitle('');
      setNewDescription('');
      navigate(`/content/${post.postId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create post.');
    }
  }

  async function onGenerateWithAi(e: React.FormEvent) {
    e.preventDefault();
    const topic = aiTopic.trim();
    if (!topic) return;
    setAiError(null);
    try {
      setAiStep('Drafting title, meta, and body…');
      const r = await aiFullMut.mutateAsync({ topic });
      const slug = r.suggestedSlug;

      setAiStep('Creating draft post…');
      const post = await createPost.mutateAsync({
        slug,
        title: r.title,
        description: r.description,
        body: r.markdown,
      });

      // Best-effort hero. If it fails, still take the user to the editor.
      try {
        setAiStep('Generating hero image…');
        const heroPrompt = `${r.title}, photorealistic, bright natural light, professional, clean composition, no text`;
        const h = await aiHeroMut.mutateAsync({ slug, prompt: heroPrompt, alt: r.title });
        await updatePost.mutateAsync({
          id: post.postId,
          patch: { hero: { src: h.src, alt: h.alt } },
        });
      } catch (heroErr) {
        console.warn('Hero generation failed:', heroErr);
      }

      setAiOpen(false);
      setAiTopic('');
      setAiStep(null);
      navigate(`/content/${post.postId}`);
    } catch (err) {
      setAiStep(null);
      setAiError(err instanceof Error ? err.message : 'AI generation failed.');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: BRAND.text }}>
            Content
          </h1>
          <p className="text-sm text-stone-500">
            Manage marketing blog posts on{' '}
            <a className="underline" href="https://fixitninjaz.com/blog" target="_blank" rel="noreferrer">
              fixitninjaz.com/blog
            </a>
            .
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-sm hover:bg-stone-50 disabled:opacity-60"
            onClick={() => void fullBuild.mutateAsync()}
            disabled={fullBuild.isPending}
            title="Re-render everything (sitemap, RSS, all posts) — use after global template changes."
          >
            {fullBuild.isPending ? 'Triggering…' : 'Full rebuild'}
          </button>
          <button
            type="button"
            className="rounded-md border border-indigo-600 bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            onClick={() => {
              setAiOpen((v) => !v);
              setAiError(null);
            }}
          >
            {aiOpen ? 'Cancel AI' : '✨ Generate with AI'}
          </button>
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm font-medium text-white shadow-sm"
            style={{ backgroundColor: BRAND.primary }}
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? 'Cancel' : '+ New post'}
          </button>
        </div>
      </div>

      {aiOpen && (
        <form
          onSubmit={onGenerateWithAi}
          className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 shadow-sm"
        >
          <h2 className="mb-1 text-sm font-semibold text-indigo-900">Generate a full post with AI</h2>
          <p className="mb-3 text-xs text-indigo-800">
            Enter a topic or keyword. The AI will draft the title, meta description, ~900-word body, slug, and hero image — then open the editor for review.
          </p>
          <label className="text-sm">
            <span className="block text-indigo-900">Topic</span>
            <textarea
              className="mt-1 w-full rounded border border-indigo-300 bg-white px-2 py-1.5"
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              rows={2}
              placeholder="e.g. how to fix a leaking kitchen tap in Dubai apartments"
              required
              minLength={5}
              maxLength={300}
              disabled={aiStep !== null}
            />
          </label>
          {aiStep && <p className="mt-2 text-sm text-indigo-700">{aiStep}</p>}
          {aiError && <p className="mt-2 text-sm text-red-600">{aiError}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-sm hover:bg-stone-50"
              onClick={() => {
                setAiOpen(false);
                setAiTopic('');
                setAiStep(null);
                setAiError(null);
              }}
              disabled={aiStep !== null}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
              disabled={aiStep !== null || !aiTopic.trim()}
            >
              {aiStep ? 'Generating…' : 'Generate full post'}
            </button>
          </div>
        </form>
      )}

      {creating && (
        <form
          onSubmit={onCreate}
          className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
        >
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Create a draft</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="text-sm">
              <span className="block text-stone-600">Slug</span>
              <input
                className="mt-1 w-full rounded border border-stone-300 px-2 py-1.5"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="ac-not-cooling-dubai"
                required
                pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
                maxLength={80}
              />
            </label>
            <label className="text-sm">
              <span className="block text-stone-600">Title</span>
              <input
                className="mt-1 w-full rounded border border-stone-300 px-2 py-1.5"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                required
                minLength={3}
                maxLength={200}
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="block text-stone-600">Meta description</span>
              <textarea
                className="mt-1 w-full rounded border border-stone-300 px-2 py-1.5"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={2}
                required
                minLength={20}
                maxLength={300}
              />
            </label>
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-stone-300 px-3 py-1.5 text-sm hover:bg-stone-50"
              onClick={() => {
                setCreating(false);
                setError(null);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded px-3 py-1.5 text-sm font-medium text-white"
              style={{ backgroundColor: BRAND.primary }}
              disabled={createPost.isPending}
            >
              {createPost.isPending ? 'Creating…' : 'Create draft'}
            </button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-stone-500">Filter:</span>
        <button
          type="button"
          className={`rounded-full px-3 py-1 ${statusFilter === 'all' ? 'bg-stone-800 text-white' : 'bg-white text-stone-700 border border-stone-300'}`}
          onClick={() => setStatusFilter('all')}
        >
          All
        </button>
        {POST_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={`rounded-full px-3 py-1 capitalize ${statusFilter === s ? 'bg-stone-800 text-white' : 'bg-white text-stone-700 border border-stone-300'}`}
            onClick={() => changeStatus(s)}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Slug</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {postsQ.isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-stone-500">
                  Loading…
                </td>
              </tr>
            )}
            {postsQ.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-stone-500">
                  No posts yet. Click <span className="font-medium">+ New post</span> to create your first draft.
                </td>
              </tr>
            )}
            {visiblePosts.map((p) => {
              const badge = STATUS_BADGE[p.status];
              return (
                <tr key={p.postId} className="hover:bg-stone-50">
                  <td className="px-3 py-2">
                    <Link to={`/content/${p.postId}`} className="font-medium text-stone-900 hover:underline">
                      {p.title}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-stone-600">{p.slug}</td>
                  <td className="px-3 py-2">
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-medium capitalize"
                      style={{ backgroundColor: badge.bg, color: badge.fg }}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-stone-600">{p.category ?? '—'}</td>
                  <td className="px-3 py-2 text-stone-500">{formatDate(p.updatedAt)}</td>
                  <td className="px-3 py-2 text-right">
                    {p.status === 'published' && (
                      <>
                        <a
                          href={`https://fixitninjaz.com/blog/${p.slug}/`}
                          target="_blank"
                          rel="noreferrer"
                          className="mr-3 text-sm text-stone-600 hover:underline"
                          title="Open the published post in a new tab"
                        >
                          View ↗
                        </a>
                        <button
                          type="button"
                          className="mr-3 text-sm text-stone-600 hover:underline disabled:opacity-50"
                          disabled={rebuildPost.isPending && rebuildPost.variables === p.postId}
                          onClick={() => void rebuildPost.mutateAsync(p.postId)}
                          title="Re-render this post and invalidate the CDN cache."
                        >
                          {rebuildPost.isPending && rebuildPost.variables === p.postId
                            ? 'Rebuilding…'
                            : 'Rebuild'}
                        </button>
                      </>
                    )}
                    <Link to={`/content/${p.postId}`} className="text-sm text-blue-600 hover:underline">
                      Edit
                    </Link>
                    <button
                      type="button"
                      className="ml-3 text-sm text-red-600 hover:underline"
                      onClick={() => {
                        if (!confirm(`Delete post "${p.title}"? This removes its markdown source too.`)) return;
                        void deletePost.mutateAsync(p.postId);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {allPosts.length > PAGE_SIZE && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-600">
            <div>
              Showing <span className="font-medium">{(safePage - 1) * PAGE_SIZE + 1}</span>–
              <span className="font-medium">{Math.min(safePage * PAGE_SIZE, allPosts.length)}</span> of{' '}
              <span className="font-medium">{allPosts.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="rounded border border-stone-300 bg-white px-2 py-1 disabled:opacity-50"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </button>
              <span className="px-2">
                Page <span className="font-medium">{safePage}</span> of {totalPages}
              </span>
              <button
                type="button"
                className="rounded border border-stone-300 bg-white px-2 py-1 disabled:opacity-50"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-stone-500">
          Recent builds
        </h2>
        <div className="overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-3 py-2">Build</th>
                <th className="px-3 py-2">Mode</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {recentBuilds.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-stone-500">
                    No builds yet.
                  </td>
                </tr>
              )}
              {recentBuilds.map((b) => (
                <tr key={b.buildId}>
                  <td className="px-3 py-2 font-mono text-xs">{b.buildId.slice(0, 12)}…</td>
                  <td className="px-3 py-2 capitalize">{b.mode}</td>
                  <td className="px-3 py-2 capitalize">
                    <span
                      className={
                        b.status === 'success'
                          ? 'text-green-700'
                          : b.status === 'failed'
                            ? 'text-red-700'
                            : 'text-stone-700'
                      }
                    >
                      {b.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-stone-500">{formatDate(b.startedAt)}</td>
                  <td className="px-3 py-2 text-stone-500">
                    {b.durationMs != null ? `${(b.durationMs / 1000).toFixed(1)}s` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}
